import React, { useCallback, useContext, useMemo } from 'react';
import { Icon, StudioMenu, type StudioMenuOption } from 'venn-ui-kit';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import type { AnalysisSubject } from 'venn-utils';
import {
  analyticsService,
  assertExhaustive,
  assertNotNil,
  MANAGE_DATA_SECTION,
  navigateToManageDataPage,
  subjectToObjectId,
  useHasFF,
  useModal,
} from 'venn-utils';
import {
  allocatorAnalysisSubject,
  analysisSubjectQuery,
  hasUnsavedChangesInPrivatesAllocator,
  hasUnsavedPortfolioChangesInAllocator,
  isAllocatorOpenState,
  isReportState,
  openAllocatorSubject,
  openPrivateAllocatorPortfolio,
  subjectInputGroupSubjects,
  type SubjectInputId,
  type SubjectWithOptionalFee,
  type SubjectWithOptionalFeeAndFilters,
  toSubjectOnly,
  useCachedLoadableValue,
  useSendSignal,
} from 'venn-state';
import { useHistory } from 'react-router-dom';
import {
  CashflowSettingsModal,
  ConfirmUnsavedChangesModal,
  UserContext,
  SingleProxyPickerContext,
} from 'venn-components';
import { SingleSubjectSearchPopover } from './SingleSubjectSearchPopover';
import { FilterPortfolioPanel } from '../../../../../../../venn-components/src/filter-portfolio/FilterPortfolioPanel';

type Action =
  | 'Manage Data'
  | 'Remove Subject'
  | 'Change Subject'
  | 'Filter Portfolio'
  | 'CF Pacing Model Parameters'
  | 'Edit Allocations'
  | 'Edit Fees'
  | 'Edit Tax Rates'
  | 'Add/Modify a Returns Proxy'
  | 'Add/Modify a Holdings Proxy';

interface SubjectActionsMenuProps {
  subject: SubjectWithOptionalFeeAndFilters;
  readonly: boolean;
  onDelete: () => void;
  onSwap: (newSubject: AnalysisSubject) => void;
  onEditFees: (subjectInputId: SubjectInputId, subject: SubjectWithOptionalFee) => void;
  onEditTaxRates: (subjectInputId: SubjectInputId, subject: SubjectWithOptionalFee) => void;
  onFilter: (groupId: SubjectInputId, portfolioId: number, filteredItemIds: string[]) => void;
  /** The subject group the subject is part of */
  groupId: SubjectInputId;
}

export const getEditFeesConfig = ({
  hasAdvisoryFeesForRlInvestmentsFF,
  hasBlockAssignmentFF,
  analysisSubject,
}: {
  hasAdvisoryFeesForRlInvestmentsFF: boolean;
  hasBlockAssignmentFF: boolean;
  analysisSubject: AnalysisSubject | undefined;
}): { shouldShowOption: false } | { shouldShowOption: true; isOptionDisabled: boolean; description?: string } => {
  if (!hasBlockAssignmentFF || !analysisSubject) {
    return { shouldShowOption: false };
  }

  switch (analysisSubject.type) {
    case 'investment':
      return { shouldShowOption: hasAdvisoryFeesForRlInvestmentsFF, isOptionDisabled: false };
    case 'private-investment':
      return { shouldShowOption: false };
    case 'portfolio': {
      if (analysisSubject.isHistoricalPortfolio) {
        return {
          shouldShowOption: true,
          isOptionDisabled: true,
          description: 'Advisory fees are not available for portfolios with historical allocations.',
        };
      }
      return { shouldShowOption: true, isOptionDisabled: false };
    }
    case 'private-portfolio':
      return {
        shouldShowOption: true,
        isOptionDisabled: true,
        description: 'Advisory fees are not available for private asset portfolios.',
      };
    default:
      return assertExhaustive(analysisSubject.type);
  }
};

export const SubjectActionsMenu = ({
  subject,
  readonly,
  onDelete,
  onSwap,
  onEditFees,
  onEditTaxRates,
  onFilter,
  groupId,
}: SubjectActionsMenuProps) => {
  const history = useHistory();
  const { open: openProxyPicker } = useContext(SingleProxyPickerContext);
  const { hasPermission } = useContext(UserContext);
  const hasProxyVisibilityFF = useHasFF('proxy_visibility_ff');
  const hasTaxImpactFF = useHasFF('tax_impact_ff');

  const hasEditHyperparametersPermission = hasPermission('EDIT_HYPERPARAMETERS');
  const hasAdvisoryFeesForRlInvestmentsFF = useHasFF('advisory_fees_for_rl_investments_ff');
  const hasBlockAssignmentFF = useHasFF('block_assignment_ff');

  const isReport = useRecoilValue(isReportState);
  const hasUnsavedReturnsAllocatorChanges = useRecoilValue(hasUnsavedPortfolioChangesInAllocator);
  const hasUnsavedPrivateAllocatorChanges = useRecoilValue(hasUnsavedChangesInPrivatesAllocator);
  const analysisSubject = useCachedLoadableValue(analysisSubjectQuery(subject), undefined);
  const groupSubjects = useRecoilValue(subjectInputGroupSubjects(groupId));

  const refreshStudioView = useSendSignal({ type: 'WorkspaceConfigurationUpdate' });

  const [isSwapOpen, openSwap, closeSwap] = useModal();
  const [isOpenFilterPortfolio, openFilterPortfolio, closeFilterPortfolio] = useModal();
  const [isCashflowSettingsModalOpen, openCashflowSettingsModal, closeCashflowSettingsModal] = useModal();
  const [isUnsavedChangesModalOpen, openUnsavedChangesModal, closeUnsavedChangesModal] = useModal();
  const otherPortfolioSubjectsQuantity = groupSubjects
    .filter((gs) => gs !== subject)
    .map((gs) => gs.filteredSubjectIds?.length || 1)
    .reduce((a, b) => a + b, 0);

  const onFilterPortfolioSubmit = (keysOfFilteredItems: string[]) => {
    subject.type === 'portfolio' && onFilter(groupId, subject.portfolioId, keysOfFilteredItems);
    closeFilterPortfolio();
  };

  const onSwapSubmit = useCallback(
    (newSubject: AnalysisSubject) => {
      closeSwap();
      onSwap(newSubject);
    },
    [closeSwap, onSwap],
  );

  const openSubjectInAllocator = useRecoilCallback(
    ({ snapshot, reset, set }) =>
      async () => {
        const openSubject = await snapshot.getPromise(openAllocatorSubject);
        openSubject && reset(allocatorAnalysisSubject(openSubject));
        set(isAllocatorOpenState, true);
        set(allocatorAnalysisSubject(toSubjectOnly(subject)), analysisSubject);
        if (analysisSubject?.private) {
          set(openPrivateAllocatorPortfolio, analysisSubject.privatePortfolio);
          set(openAllocatorSubject, undefined);
        } else {
          set(openPrivateAllocatorPortfolio, undefined);
          set(openAllocatorSubject, toSubjectOnly(subject));
        }
      },
    [subject, analysisSubject],
  );

  const onSelectOverMenuOption = useCallback(
    (option: Action) => {
      const sourceLocation = isReport ? 'Report Lab' : 'Studio';
      analyticsService.ctaClicked({
        locationOnPage: 'Subject action menu',
        purpose: option,
        text: option,
      });
      switch (option) {
        case 'Manage Data':
          if (analysisSubject?.isInvestmentInPortfolio) {
            const fundId = analysisSubject.strategy!.fund!.id;
            return navigateToManageDataPage(
              history,
              {
                fundId,
              },
              sourceLocation,
              true,
            );
          }
          return navigateToManageDataPage(history, subjectToObjectId(subject), sourceLocation, true);
        case 'CF Pacing Model Parameters':
          return hasEditHyperparametersPermission
            ? openCashflowSettingsModal()
            : navigateToManageDataPage(
                history,
                subjectToObjectId(subject),
                sourceLocation,
                true,
                MANAGE_DATA_SECTION.CASH_FLOW_PACING_SETTINGS,
              );
        case 'Remove Subject':
          return onDelete();
        case 'Change Subject':
          return openSwap();
        case 'Filter Portfolio':
          return openFilterPortfolio();
        case 'Edit Fees':
          return onEditFees(groupId, subject);
        case 'Edit Tax Rates':
          return onEditTaxRates(groupId, subject);
        case 'Edit Allocations':
          if (hasUnsavedReturnsAllocatorChanges || hasUnsavedPrivateAllocatorChanges) {
            return openUnsavedChangesModal();
          }
          return openSubjectInAllocator();
        case 'Add/Modify a Returns Proxy':
          return openProxyPicker('returnsProxy', assertNotNil(analysisSubject?.fund));
        case 'Add/Modify a Holdings Proxy':
          return openProxyPicker('holdingsProxy', assertNotNil(analysisSubject?.fund));
        default:
          return assertExhaustive(option);
      }
    },
    [
      isReport,
      analysisSubject?.isInvestmentInPortfolio,
      analysisSubject?.fund,
      analysisSubject?.strategy,
      history,
      subject,
      hasEditHyperparametersPermission,
      openCashflowSettingsModal,
      onDelete,
      openSwap,
      openFilterPortfolio,
      onEditFees,
      onEditTaxRates,
      groupId,
      hasUnsavedReturnsAllocatorChanges,
      hasUnsavedPrivateAllocatorChanges,
      openSubjectInAllocator,
      openProxyPicker,
      openUnsavedChangesModal,
    ],
  );

  const menuOptions: StudioMenuOption<Action>[] = useMemo(() => {
    const options: StudioMenuOption<Action>[] = [];

    if (
      hasProxyVisibilityFF &&
      analysisSubject?.fund &&
      analysisSubject?.fund?.assetType !== 'OPTION' &&
      analysisSubject?.fund?.assetType !== 'BENCHMARK'
    ) {
      options.push({ value: 'Add/Modify a Returns Proxy' });
      options.push({ value: 'Add/Modify a Holdings Proxy' });
    }

    if (!readonly) {
      options.push({ value: 'Change Subject' });
    }

    if (hasBlockAssignmentFF && analysisSubject?.type === 'portfolio') {
      options.push({ value: 'Filter Portfolio' });
    }

    if (analysisSubject && ['portfolio', 'private-portfolio'].includes(analysisSubject.type)) {
      options.push({ value: 'Edit Allocations' });
    }

    options.push({ value: 'Manage Data' });

    if (isReport) {
      const editFeesConfig = getEditFeesConfig({
        hasAdvisoryFeesForRlInvestmentsFF,
        hasBlockAssignmentFF,
        analysisSubject,
      });

      if (editFeesConfig.shouldShowOption) {
        options.push({
          value: 'Edit Fees',
          disabled: editFeesConfig.isOptionDisabled,
          description: editFeesConfig.description,
        });
      }

      if (hasTaxImpactFF && analysisSubject?.type === 'portfolio') {
        options.push({ value: 'Edit Tax Rates' });
      }
    }

    if (analysisSubject?.private) {
      options.push({ value: 'CF Pacing Model Parameters' });
    }

    if (!readonly) {
      options.push({ value: 'Remove Subject' });
    }

    return options;
  }, [
    readonly,
    analysisSubject,
    isReport,
    hasAdvisoryFeesForRlInvestmentsFF,
    hasBlockAssignmentFF,
    hasProxyVisibilityFF,
    hasTaxImpactFF,
  ]);

  return (
    <>
      <StudioMenu
        label="Subject Options"
        renderItem={({ value, description }) => (
          <div className="flex flex-col gap-[4px]">
            <div
              className={`flex flex-grow justify-between items-center min-w-[200px] ${value === 'Remove Subject' ? 'text-venn-error' : ''}`}
            >
              {value}
              {value === 'Manage Data' && <Icon type="arrow-up-right-from-square" className="text-[11px]" />}
            </div>
            {description && <div className="text-[12px] text-venn-grey-80">{description}</div>}
          </div>
        )}
        options={menuOptions}
        onSelect={onSelectOverMenuOption}
        size="tiny"
      />
      {isCashflowSettingsModalOpen && (
        <CashflowSettingsModal
          onChangesApplied={refreshStudioView}
          onClose={closeCashflowSettingsModal}
          privatePortfolio={analysisSubject?.privatePortfolio}
          subjectName={analysisSubject?.privateFund?.name}
          triggeringFundId={analysisSubject?.privateFund?.id}
          source="STUDIO_SUBJECTS_INPUTS"
        />
      )}
      {isOpenFilterPortfolio && analysisSubject && analysisSubject.portfolio && (
        <FilterPortfolioPanel
          portfolio={analysisSubject.portfolio}
          filteredItems={subject.filteredSubjectIds ?? []}
          otherPortfolioSubjectsQuantity={otherPortfolioSubjectsQuantity}
          handleClose={closeFilterPortfolio}
          handleApply={onFilterPortfolioSubmit}
        />
      )}
      {isSwapOpen && (
        <SingleSubjectSearchPopover
          excludedSubjects={groupSubjects}
          onClose={closeSwap}
          onSubjectSelected={onSwapSubmit}
          currentLocationForAnalytics="SubjectGroupRow"
          leftOffset={22}
        />
      )}
      {isUnsavedChangesModalOpen && (
        <ConfirmUnsavedChangesModal
          onProceed={async () => {
            await openSubjectInAllocator();
            closeUnsavedChangesModal();
          }}
          onReject={closeUnsavedChangesModal}
          ignoreUnsavedViewChanges
        />
      )}
    </>
  );
};
